import { Loader } from '@qred/components-library';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { ProcessingApplication } from '~/components/shared/ProcessingApplication/ProcessingApplication';
import { marketHasCardAutoApproval } from '~/constants/markets';
import { useAuth } from '~/contexts/AuthContext/AuthProvider';
import { ApplicationStatus } from '~/enums';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useIframeHeightSync from '~/hooks/useIframeHeightSync';
import useQueryParams from '~/hooks/useQueryParams';
import { RootState } from '~/store';
import { getCardApplication } from '~/store/actions/cardApplicationActions';
import {
  removeClearedState,
  updateForm,
} from '~/store/slices/cardApplication.slice';
import { ConfirmationPage } from './components/Steps/components/ConfirmationPage/ConfirmationPage';
import { Steps } from './components/Steps/Steps';

export const CardApplication = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { isAuthenticated } = useAuth();

  const applicationStatus = useSelector(
    (state: RootState) => state.cardApplication.applicationStatus
  );
  const applicationUuid = useSelector(
    (state: RootState) => state.cardApplication.form.applicationUuid
  );
  const cleared = useSelector(
    (state: RootState) => state.cardApplication.cleared
  );
  const market = useSelector((state: RootState) => state.intl.market);

  useIframeHeightSync();

  const queryParams = useQueryParams();
  const applicationUuidParam = queryParams.get('applicationUuid');

  const isFetchingApplication = !!applicationUuidParam && !applicationUuid;

  useEffect(() => {
    if (applicationUuidParam) {
      dispatch(getCardApplication(applicationUuidParam));
    }
  }, []);

  useEffect(() => {
    if (applicationUuid) {
      const url = new URL(globalThis.location.href);
      url.searchParams.set('applicationUuid', applicationUuid);
      history.replace({ search: url.search });
    }
  }, [applicationUuid]);

  useEffect(() => {
    if (cleared) {
      const url = new URL(globalThis.location.href);
      url.searchParams.delete('applicationUuid');
      history.replace({ search: url.search });
      dispatch(removeClearedState());
      document.cookie = `publicToken=; Max-Age=0`;
    }
  }, [cleared]);

  useEffect(() => {
    dispatch(
      updateForm({
        applicantIdentified: isAuthenticated,
        applicantAuthenticated: isAuthenticated,
      })
    );
  }, [isAuthenticated]);

  if (isFetchingApplication) {
    return <Loader m="auto" display="flex" h="100vh" />;
  }

  if (applicationStatus === ApplicationStatus.ApplicationFinished) {
    // Keyed by the applicationUuid alone: the processing page and its offer link no longer
    // need the clientId, which the store only holds when the backend returned one.
    if (marketHasCardAutoApproval[market] && applicationUuid) {
      return <ProcessingApplication applicationUuid={applicationUuid} />;
    }

    return <ConfirmationPage />;
  }

  return <Steps />;
};
